/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AccountStatusType,
  MaturityDecisionRequest,
  NominatedAccountInfoDetailType,
  PaymentProvider,
  ResponseWithAlertCodesType,
  SavingsAccountInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Observable, catchError, distinctUntilChanged, map, of } from 'rxjs';
import {
  GetAccountRequests,
  GetAvailableSavingsProducts,
  GetNominatedAccountStatus,
} from '../../actions/savings-account.action';
import { ProjectSettingsState } from '../../state/project-settings.state';
import { ProfileState } from '../../state/user-profile.state';
import { PaginatedResponse } from '../../utils/paginated-response.type';
import { AccountRepositorySearchParams } from '../account-service/account-service.service';
import { UpdateAccountResponse } from '../account-service/account.type';
import { ConfigService } from '../config-service/config-service.service';
import { KeycloakWrapperService } from '../keycloak-wrapper-service/keycloak-wrapper.service';
import {
  AccountOpenRequestType,
  AvailableSavingsProductType,
  CloseSavingsAccountRequest,
  ConnectNominatedAccountPayload,
  ConnectNominatedAccountResponse,
  CreateSavingsAccountRequest,
  InternalTransferRequest,
  NominatedAccountDetailsResponse,
  NominatedAccountStatus,
  PispDepositInitiateResponse,
  PispDepositRequest,
  PispDepositResponse,
  PispSupportedInstitution,
  ProcessNominatedAccountResponse,
  SavingsAccountInfoTypeResponse,
  SupportedInstitution,
  UpdateNominatedAccountRequest,
  WithdrawFromSavingsAccountRequest,
} from './savings-account.type';
import { buildQueryString } from '../../utils/query-params.utils';

@Injectable({
  providedIn: 'root',
})
export class SavingsAccountService {
  private baseUrl = '';
  private paymentProvider = PaymentProvider;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private store: Store,
    private keycloakWrapperService: KeycloakWrapperService,
  ) {
    this.configService.getApi('account_service').subscribe((response) => {
      this.baseUrl = response;
      this.dispatchActionToFetchNominatedAccountStatus();
    });
  }

  private dispatchActionToFetchNominatedAccountStatus() {
    this.store
      .select(ProfileState.getProfile)
      //only execute the subscription callback when the profile object changes semantically (deep-equality by value), preventing redundant dispatches on identical data references.
      .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
      .subscribe((profile) => {
        const projectSettings = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings);
        const isUserLoggedInAndVerified =
          this.keycloakWrapperService.isLoggedIn() && profile.kycVerificationStatus === 'success';
        if (
          isUserLoggedInAndVerified &&
          (projectSettings.provider === this.paymentProvider.THISBANK ||
            projectSettings.provider === this.paymentProvider.SAASCADA)
        ) {
          this.store.dispatch(new GetNominatedAccountStatus());
          this.store.dispatch(new GetAccountRequests());
          this.store.dispatch(new GetAvailableSavingsProducts());
        }
      });
  }

  public fetchNominatedAccountStatus(): Observable<NominatedAccountStatus> {
    return this.http.get<NominatedAccountStatus>(`${this.baseUrl}/savings/nominated-account-status`).pipe(
      catchError((error) => {
        console.error('Error getting nominated account status', error);
        throw error;
      }),
    );
  }

  public fetchSupportedAispInstitutions(country: string): Observable<SupportedInstitution[]> {
    return this.http
      .get<SupportedInstitution[]>(`${this.baseUrl}/savings/supported-aisp-institutions?countryCode=${country}`)
      .pipe(
        catchError((error) => {
          console.error('Error getting supported aisp institutions', error);
          throw error;
        }),
      );
  }

  public fetchSupportedPispInstitutions(
    country: string,
    feature?: string,
  ): Observable<PispSupportedInstitution[]> {
    const featureParam = feature ? `&feature=${feature}` : '';
    return this.http
      .get<PispSupportedInstitution[]>(
        `${this.baseUrl}/savings/supported-pisp-institutions?country=${country}${featureParam}`,
      )
      .pipe(
        map((response) => response),
        catchError((error) => {
          console.error('Error getting supported pisp institutions', error);
          throw error;
        }),
      );
  }

  public getSavingAccounts(
    params: AccountRepositorySearchParams,
  ): Observable<PaginatedResponse<SavingsAccountInfoType>> {
    return this.http
      .get<PaginatedResponse<SavingsAccountInfoType>>(`${this.baseUrl}/savings/`, {
        params: {
          ...params,
          status: params.status ?? [AccountStatusType.active],
          page: params.page ?? 1,
          size: params.size ?? 10,
        },
      })
      .pipe(
        map((v: PaginatedResponse<SavingsAccountInfoType>) => {
          return v;
        }),
        catchError<
          PaginatedResponse<SavingsAccountInfoType>,
          Observable<PaginatedResponse<SavingsAccountInfoType>>
        >((_err, _caught) => {
          console.error(`Error getting accounts: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public updateSavingAccount(
    accountId: string,
    update: { name?: string },
  ): Observable<UpdateAccountResponse> {
    return this.http.patch<UpdateAccountResponse>(`${this.baseUrl}/savings/${accountId}`, update);
  }

  public getActiveSavingAccountDetails(accountId: string): Observable<SavingsAccountInfoType> {
    return this.http.get<SavingsAccountInfoType>(`${this.baseUrl}/savings/${accountId}`);
  }

  public connectNominatedAccount(
    payload: ConnectNominatedAccountPayload,
  ): Observable<ConnectNominatedAccountResponse> {
    return this.http.post<ConnectNominatedAccountResponse>(
      `${this.baseUrl}/savings/connect-nominated-account`,
      payload,
    );
  }

  public processNominatedAccount(
    requestId: string,
    accountId?: string,
  ): Observable<ProcessNominatedAccountResponse> {
    return this.http.post<ProcessNominatedAccountResponse>(
      `${this.baseUrl}/savings/process-nominated-account`,
      { requestId, accountId },
    );
  }

  public fetchNominatedAccountDetails(accounts: string[]): Observable<NominatedAccountDetailsResponse[]> {
    return this.http.post<NominatedAccountDetailsResponse[]>(
      `${this.baseUrl}/savings/nominated-account-details`,
      { accountIds: accounts },
    );
  }

  public getAvailableProducts(payload?: {
    page?: number;
    limit?: number;
    category?: string;
    clientType?: string;
    accountType?: string;
  }): Observable<AvailableSavingsProductType[]> {
    let url = `${this.baseUrl}/savings/available-products`;
    url = this.modifyUrlWithQueryParams(url, payload);
    return this.http.get<AvailableSavingsProductType[]>(url).pipe(
      catchError((error) => {
        console.error('Error getting available savings products', error);
        return [];
      }),
    );
  }

  /**
   * The signed-in customer's savings catalogue — the org-scoped counterpart of
   * getAvailableProducts. Deliberately parameterless: the backend route takes
   * no filters, because the provider seam behind it accepts an organisation id
   * and nothing else.
   */
  public getCustomerAvailableProducts(): Observable<AvailableSavingsProductType[]> {
    return this.http.get<AvailableSavingsProductType[]>(
      `${this.baseUrl}/savings/customer-available-products`,
    );
  }

  private modifyUrlWithQueryParams(
    url: string,
    payload:
      | {
          page?: number;
          limit?: number;
          category?: string;
          clientType?: string;
          accountType?: string;
        }
      | undefined,
  ): string {
    if (!payload) {
      return url;
    }
    const queryParams = {
      page: payload.page,
      size: payload.limit, // Maps limit → size
      category: payload.category,
      clientType: payload.clientType,
      accountType: payload.accountType,
    };
    url += buildQueryString(queryParams);
    return url;
  }

  /**
   * `status` is optional on the route: omitting it answers every request the
   * organisation has, whatever its state. It defaults to 'awaiting-approval'
   * here because that is what every existing caller (the account-request listing
   * widget, via the Ngxs state) has always asked for — pass `undefined`
   * explicitly for the unfiltered list.
   */
  public getAccountRequests(
    status: 'approved' | 'rejected' | 'awaiting-approval' | 'cancelled' | undefined = 'awaiting-approval',
  ): Observable<AccountOpenRequestType[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http
      .get<AccountOpenRequestType[]>(`${this.baseUrl}/savings/account-requests${query}`)
      .pipe(
        catchError((error) => {
          console.error('Error getting account requests', error);
          throw error;
        }),
      );
  }

  public createSavingsAccount(
    payload: CreateSavingsAccountRequest,
  ): Observable<ResponseWithAlertCodesType<SavingsAccountInfoTypeResponse>> {
    return this.http
      .post<ResponseWithAlertCodesType<SavingsAccountInfoTypeResponse>>(`${this.baseUrl}/savings`, payload)
      .pipe(
        catchError((error) => {
          console.error('Error creating savings account', error);
          throw error;
        }),
      );
  }

  public getAccountRequestToken(requestId: string): Observable<{
    status: 'approved' | 'rejected' | 'awaiting-approval' | 'manual-review';
    sumsubAccessToken?: string;
  }> {
    return this.http
      .post<{ status: 'approved' | 'rejected' | 'awaiting-approval'; sumsubAccessToken?: string }>(
        `${this.baseUrl}/savings/kyc-account-request/${requestId}`,
        {},
      )
      .pipe(
        catchError((error) => {
          console.error('Error getting account request token', error);
          throw error;
        }),
      );
  }

  public withdrawFromSavingsAccount(
    accountId: string,
    requestPayload: WithdrawFromSavingsAccountRequest,
  ): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/savings/${accountId}/withdraw`, { ...requestPayload });
  }

  public createPispBillingRequest(
    accountId: string,
    payload: { amount: number; isRecurring?: boolean },
  ): Observable<{ billingRequestId: string }> {
    return this.http.post<{ billingRequestId: string }>(
      `${this.baseUrl}/savings/${accountId}/pisp-billing-request`,
      payload,
    );
  }

  public initiatePispDeposit(
    accountId: string,
    payload: PispDepositRequest,
  ): Observable<PispDepositInitiateResponse> {
    return this.http.post<any>(`${this.baseUrl}/savings/${accountId}/pisp-deposit`, { ...payload });
  }

  public checkPispDepositStatus(
    pispDepositRequestId: string,
    accountId: string,
  ): Observable<PispDepositResponse> {
    return this.http.post<any>(
      `${this.baseUrl}/savings/${accountId}/check-pisp-deposit/${pispDepositRequestId}`,
      {},
    );
  }

  public setPispInstituionOnNomiantedAccount(
    pispInstitutionId: string | null,
  ): Observable<PispDepositResponse> {
    return this.http.post<any>(`${this.baseUrl}/savings/pisp-set-institution`, {
      pispInstitutionId: pispInstitutionId,
    });
  }

  public fetchNominatedAccountInformation(
    nominatedAccountId: string,
  ): Observable<NominatedAccountInfoDetailType> {
    return this.http.get<NominatedAccountInfoDetailType>(`${this.baseUrl}/savings/nominated-account`);
  }

  public initiateInternalTransfer(fromAccountId: string, payload: InternalTransferRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/savings/${fromAccountId}/internal-transfer`, payload);
  }

  public submitMaturityDecision(payload: MaturityDecisionRequest, accountId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/savings/${accountId}/maturity-decision`, payload);
  }

  public closeSavingsAccount(accountId: string, payload: CloseSavingsAccountRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/savings/${accountId}/close`, payload);
  }

  public updateNominatedAccount(payload: UpdateNominatedAccountRequest) {
    return this.http.patch(`${this.baseUrl}/savings/nominated-account`, payload);
  }
}
